import { ChangeEvent, KeyboardEvent, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { LoadingSpinner } from '@/shared/components/Loading';
import { addButtonStyle, darkPanelStyle, darkTextDim, darkDangerText } from '@/shared/dark-inline-styles';
import { COURSE_CRM_SHIPPING_STATUSES, COURSE_CRM_STATUS_LABELS, CourseCrmShippingStatus, CrmEnrollmentType } from '@/models/course-crm';
import { CourseCrmFilters, useCourseCrmClients } from '@/services/course-crm';
import MatTableHead from './components/MatTableHead';
import StatusChip, { TypeChip } from './components/status-chip';
import ClientModal from './components/client-modal';
import ImportModal from './components/import-modal';

const Kpi = ({ n, label, sub, alert }: { n: number; label: string; sub?: string; alert?: boolean }) => (
    <div style={{ ...darkPanelStyle, flex: '1 1 150px', minWidth: 150 }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: alert && n > 0 ? darkDangerText : undefined }}>{n}</div>
        <div style={{ fontSize: 11.5, color: '#cccccc' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: darkTextDim, marginTop: 4 }}>{sub}</div>}
    </div>
);

/**
 * Course CRM — one row per course client, joining their account, their cohort
 * and the Harmony Board shipment.
 *
 * Everything here is a PROJECTION: the CRM labels are saved back onto the user,
 * the shipment onto the fulfilment order the Orders screen also edits. There is
 * no separate client record, so the two screens can never disagree.
 */
const CourseCrm = () => {
    const [page, setPage] = useState(1); // 1-indexed for the API
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [type, setType] = useState<CrmEnrollmentType | ''>('');
    const [cohortId, setCohortId] = useState('');
    const [status, setStatus] = useState<CourseCrmShippingStatus | ''>('');

    const [openClientId, setOpenClientId] = useState<string>();
    const [focusTracking, setFocusTracking] = useState(false);
    const [importOpen, setImportOpen] = useState(false);

    // Memoised so the object identity is stable — it is part of the query key,
    // and a fresh object every render would refetch on every render.
    const filters: CourseCrmFilters = useMemo(
        () => ({
            page,
            limit: rowsPerPage,
            search: search || undefined,
            type: type || undefined,
            cohortId: cohortId || undefined,
            status: status || undefined,
        }),
        [page, rowsPerPage, search, type, cohortId, status],
    );

    const { data: res, isLoading } = useCourseCrmClients(filters);
    const result = res?.data;
    const clients = result?.clients ?? [];
    const summary = result?.summary;
    const cohorts = result?.cohorts ?? [];

    const applySearch = () => {
        setSearch(searchInput.trim());
        setPage(1);
    };
    const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') applySearch();
    };
    const handleChangePage = (_: unknown, item: number) => setPage(item + 1); // TablePagination is 0-indexed
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    const openClient = (userId: string, tracking = false) => {
        setFocusTracking(tracking);
        setOpenClientId(userId);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Course CRM</h3>
                    <h3 className="page-subhead subhead">
                        Course clients and their Harmony Board shipment — sign-up, Telegram, tracking code and delivery, in one place.
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Card>
                        <CardBody>
                            {/* KPI row. The counts describe the FILTERED table below,
                                so the numbers always match what is on screen. */}
                            {summary && (
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                                    <Kpi n={summary.total} label="Clients" sub={`${summary.paid} paid · ${summary.gift} gift`} />
                                    <Kpi n={summary.notStarted} label="Not started" sub="no address / not sent to shipper" />
                                    <Kpi n={summary.awaitingShipment} label="Awaiting shipment" sub="with the shipper, no code yet" />
                                    <Kpi
                                        n={summary.shipped + summary.delivered}
                                        label="Shipped / delivered"
                                        sub={`${summary.shipped} in transit · ${summary.delivered} delivered`}
                                    />
                                    <Kpi n={summary.needsAttention} label="Needs attention" sub="refunds, exceptions, no contact" alert />
                                    <Kpi n={summary.missingTrackingCode} label="Shipped, no code" sub="a code is still owed for these" alert />
                                </div>
                            )}

                            <Row className="align-items-center">
                                <Col xs={12} lg={4}>
                                    <MaterialTextField
                                        label="Search name, email or TG handle (press Enter)"
                                        type="text"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyDown={onSearchKeyDown}
                                        onBlur={applySearch}
                                    />
                                </Col>
                                <Col xs={12} lg={2}>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Type"
                                        value={type}
                                        onChange={(e) => {
                                            setType(e.target.value as CrmEnrollmentType | '');
                                            setPage(1);
                                        }}
                                    >
                                        <MaterialFormOption value="">All types</MaterialFormOption>
                                        <MaterialFormOption value="paid">Paid</MaterialFormOption>
                                        <MaterialFormOption value="gift">Gift</MaterialFormOption>
                                    </MaterialTextField>
                                </Col>
                                <Col xs={12} lg={3}>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Cohort"
                                        value={cohortId}
                                        onChange={(e) => {
                                            setCohortId(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MaterialFormOption value="">All cohorts</MaterialFormOption>
                                        {cohorts.map((c) => (
                                            <MaterialFormOption key={c.id} value={c.id}>
                                                {c.title}
                                            </MaterialFormOption>
                                        ))}
                                        {/* A real filter value, not a missing one: "who is in no
                                            cohort" is the question the blank Cohort cells raise. */}
                                        <MaterialFormOption value="unassigned">Unassigned</MaterialFormOption>
                                    </MaterialTextField>
                                </Col>
                                <Col xs={12} lg={3}>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Shipping status"
                                        value={status}
                                        onChange={(e) => {
                                            setStatus(e.target.value as CourseCrmShippingStatus | '');
                                            setPage(1);
                                        }}
                                    >
                                        <MaterialFormOption value="">All statuses</MaterialFormOption>
                                        {COURSE_CRM_SHIPPING_STATUSES.map((s) => (
                                            <MaterialFormOption key={s} value={s}>
                                                {COURSE_CRM_STATUS_LABELS[s]}
                                            </MaterialFormOption>
                                        ))}
                                    </MaterialTextField>
                                </Col>
                            </Row>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                                <button type="button" style={addButtonStyle} onClick={() => setImportOpen(true)}>
                                    Import from spreadsheet
                                </button>
                            </div>

                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead />
                                            <TableBody>
                                                {clients.map((c) => (
                                                    <TableRow role="checkbox" tabIndex={-1} key={c.userId} onClick={() => openClient(c.userId)}>
                                                        <TableCell component="th" scope="row" padding="none">
                                                            <div style={{ fontWeight: 600 }}>{c.name}</div>
                                                            <div style={{ fontSize: 11.5, color: darkTextDim }}>{c.email || 'no email on file'}</div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <TypeChip type={c.enrollmentType} />
                                                        </TableCell>
                                                        <TableCell>
                                                            {c.cohortLabel ?? <span style={{ color: darkTextDim }}>—</span>}
                                                            {!c.cohortId && c.cohortLabel && (
                                                                <span style={{ display: 'block', fontSize: 10.5, color: darkTextDim }}>
                                                                    label only
                                                                </span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{c.country ?? <span style={{ color: darkTextDim }}>—</span>}</TableCell>
                                                        <TableCell>
                                                            {c.telegramJoined ? (
                                                                <span style={{ color: '#6ddba0' }}>✓</span>
                                                            ) : (
                                                                <span style={{ color: darkTextDim }}>—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <StatusChip status={c.shippingStatus} />
                                                        </TableCell>
                                                        <TableCell>
                                                            {c.trackingCode ? (
                                                                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                                                                    {c.trackingCode}
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    style={{ ...addButtonStyle, padding: '3px 9px', fontSize: 11.5 }}
                                                                    onClick={(e) => {
                                                                        // The row itself opens the drawer; this opens it
                                                                        // straight onto the field the admin came for.
                                                                        e.stopPropagation();
                                                                        openClient(c.userId, true);
                                                                    }}
                                                                >
                                                                    + Add code
                                                                </button>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {c.trackingStatusDetail ? (
                                                                <span style={{ fontSize: 12 }}>
                                                                    {c.trackingStatusDetail}
                                                                    {c.trackingLastCheckedAt && (
                                                                        <span style={{ color: darkTextDim, display: 'block', fontSize: 11 }}>
                                                                            {new Date(c.trackingLastCheckedAt).toLocaleString()}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            ) : c.trackingLastError ? (
                                                                <span style={{ color: darkDangerText, fontSize: 11 }}>Last check failed</span>
                                                            ) : (
                                                                <span style={{ color: darkTextDim }}>—</span>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {clients.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={8} className="text-center">
                                                            No clients match these filters.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={result?.total ?? 0}
                                        rowsPerPage={rowsPerPage}
                                        page={page - 1}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[25, 50, 100]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <ClientModal
                isOpen={!!openClientId}
                userId={openClientId}
                focusTracking={focusTracking}
                toggle={() => {
                    setOpenClientId(undefined);
                    setFocusTracking(false);
                }}
            />
            <ImportModal isOpen={importOpen} toggle={() => setImportOpen(false)} />
        </Container>
    );
};

export default CourseCrm;
